import { track } from '../../analytics/analytics';
import { CLASSIC_SHOP_URL } from '../../app/constants';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface ChatResponse {
  reply?: string;
  escalate?: boolean;
  error_code?: string;
}

export interface Conseiller {
  element: HTMLElement;
  /** `anchor` est le personnage : la bulle s'accroche à lui. */
  open(anchor?: HTMLElement): void;
  close(): void;
  isOpen(): boolean;
  destroy(): void;
}

const BACKEND = import.meta.env.VITE_CHAT_BACKEND_URL ?? '';

const ACCUEIL =
  'Bonjour. Je suis derrière le comptoir. Une question sur une mastication, une commande, un chien difficile ?';

/**
 * Le conseiller du comptoir : c'est le chatbot du site, appelé directement.
 * Sans backend configuré, le panneau reste utile et renvoie vers les canaux
 * humains plutôt que d'afficher une conversation morte.
 */
export function createConseiller(parent: HTMLElement): Conseiller {
  const element = document.createElement('aside');
  element.className = 'conseiller';
  element.setAttribute('role', 'dialog');
  element.setAttribute('aria-label', 'Le conseiller du comptoir');
  element.hidden = true;

  // Queue du phylactère : elle pointe vers le personnage qui parle.
  const queue = document.createElement('span');
  queue.className = 'conseiller__tail';
  queue.setAttribute('aria-hidden', 'true');

  const entete = document.createElement('div');
  entete.className = 'conseiller__header';

  const identite = document.createElement('div');
  identite.className = 'conseiller__identity';

  const avatar = document.createElement('img');
  avatar.className = 'conseiller__avatar';
  avatar.src = '/props/prop-conseiller-avatar-carre.webp';
  avatar.alt = '';
  // Sans image, l'en-tête reste propre : on retire simplement la pastille.
  avatar.addEventListener('error', () => avatar.remove());

  const titre = document.createElement('p');
  titre.className = 'conseiller__title';
  titre.textContent = 'Le conseiller';
  identite.append(avatar, titre);

  const fermer = document.createElement('button');
  fermer.type = 'button';
  fermer.className = 'conseiller__close';
  fermer.setAttribute('aria-label', 'Fermer la conversation');
  fermer.textContent = '×';
  entete.append(identite, fermer);

  const fil = document.createElement('div');
  fil.className = 'conseiller__thread';
  fil.setAttribute('role', 'log');
  fil.setAttribute('aria-live', 'polite');

  const formulaire = document.createElement('form');
  formulaire.className = 'conseiller__form';

  const champ = document.createElement('input');
  champ.type = 'text';
  champ.className = 'conseiller__input';
  champ.placeholder = 'Votre question…';
  champ.setAttribute('aria-label', 'Votre question au conseiller');
  champ.autocomplete = 'off';

  const envoyer = document.createElement('button');
  envoyer.type = 'submit';
  envoyer.className = 'conseiller__send';
  envoyer.textContent = 'Envoyer';

  formulaire.append(champ, envoyer);
  element.append(entete, fil, formulaire, queue);
  parent.append(element);

  const historique: Message[] = [];
  let lastFocused: HTMLElement | null = null;
  let ancre: HTMLElement | null = null;

  /**
   * Place la bulle à côté du personnage, du côté où il y a de la place, et
   * oriente la queue vers lui. Sur petit écran la bulle occupe le bas de
   * l'écran : l'ancrage n'aurait plus de sens.
   */
  function placer(): void {
    if (element.hidden) return;
    if (window.innerWidth < 768 || !ancre) {
      element.removeAttribute('style');
      element.dataset['cote'] = 'bas';
      return;
    }

    const cible = ancre.getBoundingClientRect();
    const bulleRect = element.getBoundingClientRect();
    const marge = 16;

    const placeADroite = window.innerWidth - cible.right;
    const aDroite = placeADroite > bulleRect.width + marge * 2;
    const left = aDroite
      ? cible.right + marge
      : Math.max(marge, cible.left - bulleRect.width - marge);

    let top = cible.top + cible.height / 2 - bulleRect.height / 2;
    top = Math.max(marge, Math.min(top, window.innerHeight - bulleRect.height - marge));

    element.style.left = `${Math.round(left)}px`;
    element.style.top = `${Math.round(top)}px`;
    element.style.right = 'auto';
    element.style.bottom = 'auto';
    element.dataset['cote'] = aDroite ? 'gauche' : 'droite';

    // La queue vise le personnage, même si la bulle a glissé pour tenir à l'écran.
    const pointe = cible.top + cible.height * 0.4 - top;
    queue.style.top = `${Math.round(Math.max(24, Math.min(pointe, bulleRect.height - 40)))}px`;
  }

  function bulle(role: Message['role'] | 'systeme', texte: string): HTMLElement {
    const ligne = document.createElement('p');
    ligne.className = `conseiller__msg conseiller__msg--${role}`;
    ligne.textContent = texte;
    fil.append(ligne);
    fil.scrollTop = fil.scrollHeight;
    return ligne;
  }

  function proposerHumain(): void {
    const bloc = document.createElement('div');
    bloc.className = 'conseiller__escalade';
    const texte = document.createElement('p');
    texte.textContent = 'Un humain fera mieux que moi sur ce coup.';
    const lien = document.createElement('a');
    lien.className = 'conseiller__link';
    lien.href = `${CLASSIC_SHOP_URL}/pages/contact`;
    lien.target = '_blank';
    lien.rel = 'noopener noreferrer';
    lien.textContent = 'Écrire à Chien Heureux';
    bloc.append(texte, lien);
    fil.append(bloc);
    fil.scrollTop = fil.scrollHeight;
  }

  async function demander(question: string): Promise<void> {
    historique.push({ role: 'user', content: question });
    bulle('user', question);

    if (!BACKEND) {
      // Backend non configuré : on le dit franchement au lieu de simuler.
      bulle(
        'systeme',
        'Le conseiller n’est pas joignable depuis cette boutique pour le moment.',
      );
      proposerHumain();
      return;
    }

    const attente = bulle('assistant', '…');
    envoyer.disabled = true;
    champ.disabled = true;

    try {
      const response = await fetch(BACKEND, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: historique }),
      });
      const data = (await response.json()) as ChatResponse;
      const reponse = data.reply ?? 'Je n’ai pas de réponse à cette question.';
      attente.textContent = reponse;
      historique.push({ role: 'assistant', content: reponse });
      track('hotspot_clicked', { source: 'conseiller' });
      if (data.escalate) proposerHumain();
    } catch {
      attente.textContent = 'La liaison avec le comptoir a été coupée.';
      proposerHumain();
    } finally {
      envoyer.disabled = false;
      champ.disabled = false;
      champ.focus();
    }
  }

  formulaire.addEventListener('submit', (event) => {
    event.preventDefault();
    const question = champ.value.trim();
    if (!question) return;
    champ.value = '';
    void demander(question);
  });

  function hide(): void {
    element.hidden = true;
    element.classList.remove('is-open');
    lastFocused?.focus();
    lastFocused = null;
  }

  fermer.addEventListener('click', hide);
  window.addEventListener('resize', placer);
  // Le décor se parcourt latéralement : la bulle suit le personnage.
  document.addEventListener('scroll', placer, { capture: true, passive: true });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !element.hidden) hide();
  });

  return {
    element,
    open(anchor?: HTMLElement): void {
      if (fil.childElementCount === 0) bulle('assistant', ACCUEIL);
      ancre = anchor ?? null;
      lastFocused = document.activeElement as HTMLElement | null;
      element.hidden = false;
      requestAnimationFrame(() => {
        placer();
        element.classList.add('is-open');
        champ.focus();
      });
    },
    close: hide,
    isOpen: () => !element.hidden,
    destroy(): void {
      element.remove();
    },
  };
}
